import type {
  CustomSkillEntity,
  ExprNode,
  UserNode,
} from '@revelio/data-access';

import type { CustomSkillTaxonomySelection } from '../../../filters/definitions/skillTaxonomy';
import { findFilterById } from '../../../filters/engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  type SingleValueFilter,
} from '../../../filters/types/filters';
import { getCustomSkillFilter } from '../../../filters/utils/getCustomSkillFilter';
import { buildOrNode, buildValNode, wrapExprNode } from './node-builders';
import { TalentDiscoveryV3Tags } from './types';

/**
 * Map skill level_id (1-5) to V3 tag
 */
const getSkillLevelTag = (levelId: number): TalentDiscoveryV3Tags | null => {
  switch (levelId) {
    case 1:
      return TalentDiscoveryV3Tags.SkillLvl_1;
    case 2:
      return TalentDiscoveryV3Tags.SkillLvl_2;
    case 3:
      return TalentDiscoveryV3Tags.SkillLvl_3;
    case 4:
      return TalentDiscoveryV3Tags.SkillLvl_4;
    case 5:
      return TalentDiscoveryV3Tags.SkillLvl_5;
    default:
      return null;
  }
};

/**
 * Build a skill val node from a skill entity (returns ExprNode)
 */
const buildSkillValNode = (entity: CustomSkillEntity): ExprNode | null => {
  const tag = getSkillLevelTag(entity.level_id);
  if (!tag) return null;

  return buildValNode(tag, entity.entity_id, true);
};

/**
 * Build an OR node for skills within a single instance (group) (returns ExprNode)
 */
const buildSkillInstanceNode = (
  entities: CustomSkillEntity[]
): ExprNode | null => {
  if (entities.length === 0) return null;

  const skillNodes = entities
    .map(buildSkillValNode)
    .filter((node): node is ExprNode => node !== null);

  if (skillNodes.length === 0) return null;
  if (skillNodes.length === 1) {
    const [singleNode] = skillNodes;
    return singleNode ?? null;
  }

  return buildOrNode(skillNodes);
};

/**
 * Build custom skill filter node from filter state.
 * This is the single-filter (non-MULTI) variant used on non-TD pages.
 */
export const buildCustomSkillFilter = (filters: Filter[]): UserNode | null => {
  const customSkillFilter = findFilterById(filters, FilterId.CUSTOM_SKILL);
  if (!customSkillFilter || customSkillFilter.type !== FilterType.TREE) {
    return null;
  }

  const serialised = getCustomSkillFilter(customSkillFilter, filters);
  const customSkill = serialised.custom_skill?.entities;

  if (customSkill && customSkill.length > 0) {
    const exprNode = buildSkillInstanceNode(customSkill);
    if (!exprNode) return null;

    return wrapExprNode(exprNode, customSkillFilter.isNot);
  }

  return null;
};

/**
 * Extract skill taxonomy ID from filters
 */
export const getSkillTaxonomyId = (filters: Filter[]): number => {
  const skillTaxonomyFilter = findFilterById(filters, FilterId.SKILL_TAXONOMY);
  if (skillTaxonomyFilter && skillTaxonomyFilter.type === FilterType.VALUE) {
    const valueObj = (
      skillTaxonomyFilter as SingleValueFilter<CustomSkillTaxonomySelection>
    ).value as CustomSkillTaxonomySelection | undefined;
    if (valueObj && typeof valueObj.taxonomyId === 'number') {
      return valueObj.taxonomyId;
    }
  }
  return 0; // default
};

/**
 * Build multi custom skill filter node from filter state.
 * Each instance becomes an OR group, and instances are ANDed together.
 * Returns an array of nodes when there are multiple instances (to avoid nested AND nodes).
 */
export const buildMultiCustomSkillFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiCustomSkill = findFilterById(filters, FilterId.MULTI_CUSTOM_SKILL);

  if (!multiCustomSkill || !isMultiFilter(multiCustomSkill)) {
    return null;
  }

  if (multiCustomSkill.instances.length === 0) {
    return null;
  }

  const instanceNodes = multiCustomSkill.instances
    .map((instance) => {
      const serialised = getCustomSkillFilter(instance, filters);
      const customSkill = serialised.custom_skill?.entities;

      if (customSkill && customSkill.length > 0) {
        const exprNode = buildSkillInstanceNode(customSkill);
        if (!exprNode) return null;

        return wrapExprNode(exprNode, instance.isNot);
      }
      return null;
    })
    .filter((node): node is UserNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};
